/**
 * Paste planning: the pure half of the Clipboard_Manager
 * (Requirements 12.6, 12.7, 12.10, 4.12).
 *
 * {@link planPaste} turns a {@link ClipPayload}, a destination, and a variant into a
 * plan: the affected range, whether the destination had to expand, the typed text to
 * write per cell, and the style to apply per cell. It touches no workbook, so the
 * five variants' laws are testable without a store, which is what Property 48 does.
 *
 * The five variants, and what each one writes:
 *
 * | Variant | Values | Formulas | Formatting |
 * | --- | --- | --- | --- |
 * | `all` | yes | translated | yes |
 * | `values` | yes | flattened to their computed value | no |
 * | `formulas` | yes | translated | no |
 * | `formats` | no | no | yes |
 * | `transpose` | yes | translated | yes, transposed |
 *
 * **Formula translation.** A formula cell is written as its source translated by the
 * offset between where it was copied from and where it lands, through
 * `formula/refs.ts#translate` — so relative components shift, `$`-anchored ones do
 * not, and a reference pushed off the sheet becomes `#REF!` (Requirement 4.12,
 * Property 16). The offset is measured from `payload.origin`; a payload from outside
 * the app has no origin and carries no formulas, so nothing translates.
 *
 * **Destination expansion.** The block always lands at its full size. When the user
 * had a *smaller* range selected, the plan reports `expanded: true` and the affected
 * range, which the dispatcher turns into the notice Requirement 12.10 asks for. A
 * larger selection is not tiled: pasting a 2×2 block into a 10×10 selection writes
 * four cells, because guessing that the user wanted twenty-five copies is a worse
 * failure than writing what they copied.
 */

import { parseFormula } from '../formula/parser.js';
import { printFormula } from '../formula/printer.js';
import { translate } from '../formula/refs.js';
import type { CellStyle } from '../format/style.js';
import { MAX_COLS, MAX_ROWS, normalizeRange, type Addr, type Range } from '../types.js';
import type { ClipPayload, PasteVariant } from '../command/types.js';

/** What a paste will write, before anything is validated against the store. */
export interface PastePlan {
  /** The cells the paste covers, at the payload's dimensions (Requirement 12.10). */
  target: Range;
  /** True when the destination selection was smaller than the payload. */
  expanded: boolean;
  /** The selection the user had, for the expansion notice. */
  requested: Range;
  /** Typed text per cell of `target`, or `null` to leave the value alone. */
  inputs: (string | null)[][];
  /** Style per cell of `target`, or `null` to leave the formatting alone. */
  styles: (CellStyle | null)[][];
  /** True when the variant writes values or formulas. */
  writesValues: boolean;
  /** True when the variant writes formatting. */
  writesStyles: boolean;
}

/** The dimensions a payload occupies once `variant` is applied. */
export function pasteSize(payload: ClipPayload, variant: PasteVariant): { rows: number; cols: number } {
  const rows = Math.max(0, Math.trunc(payload.rows));
  const cols = Math.max(0, Math.trunc(payload.cols));
  return variant === 'transpose' ? { rows: cols, cols: rows } : { rows, cols };
}

function at(
  matrix: readonly (readonly (string | null)[])[] | undefined,
  row: number,
  col: number,
): string | null {
  return matrix?.[row]?.[col] ?? null;
}

/**
 * Plans a paste (Requirements 12.6, 12.7, 12.10).
 *
 * `anchor` is the cell the block's top-left lands on. `selection` is the range the
 * user had selected, and only its size matters — the anchor already carries the
 * position. `null` means a single cell was selected, which is never an expansion.
 */
export function planPaste(
  payload: ClipPayload,
  anchor: Addr,
  variant: PasteVariant,
  selection: Range | null = null,
): PastePlan {
  const { rows, cols } = pasteSize(payload, variant);
  const target: Range = {
    sheet: anchor.sheet,
    r0: anchor.row,
    c0: anchor.col,
    r1: anchor.row + Math.max(rows, 1) - 1,
    c1: anchor.col + Math.max(cols, 1) - 1,
  };
  const requested =
    selection === null
      ? { sheet: anchor.sheet, r0: anchor.row, c0: anchor.col, r1: anchor.row, c1: anchor.col }
      : normalizeRange(selection);
  const selRows = requested.r1 - requested.r0 + 1;
  const selCols = requested.c1 - requested.c0 + 1;
  const expanded = selection !== null && (selRows < rows || selCols < cols);

  const writesValues = variant !== 'formats';
  const writesStyles = variant === 'all' || variant === 'formats' || variant === 'transpose';
  const transposed = variant === 'transpose';

  const inputs: (string | null)[][] = [];
  const styles: (CellStyle | null)[][] = [];
  for (let i = 0; i < rows; i++) {
    const inputRow: (string | null)[] = [];
    const styleRow: (CellStyle | null)[] = [];
    for (let j = 0; j < cols; j++) {
      const si = transposed ? j : i;
      const sj = transposed ? i : j;
      inputRow.push(
        writesValues
          ? cellInput(payload, si, sj, variant, { sheet: target.sheet, row: target.r0 + i, col: target.c0 + j })
          : null,
      );
      styleRow.push(writesStyles ? styleOf(payload, si, sj) : null);
    }
    inputs.push(inputRow);
    styles.push(styleRow);
  }

  return { target, expanded, requested, inputs, styles, writesValues, writesStyles };
}

/** The style a source cell carried, or `null` when the payload has none. */
function styleOf(payload: ClipPayload, row: number, col: number): CellStyle | null {
  const index = payload.styles?.[row]?.[col];
  if (index === undefined) return null;
  return payload.palette?.[index] ?? null;
}

/**
 * The typed text one destination cell receives.
 *
 * The values-only variant always writes the value text, which for a formula cell is
 * its computed result — that is what "values only" means. Every other value-writing
 * variant writes the translated formula source when there is one.
 */
function cellInput(
  payload: ClipPayload,
  row: number,
  col: number,
  variant: PasteVariant,
  dest: Addr,
): string {
  const value = at(payload.inputs, row, col) ?? '';
  if (variant === 'values') return value;
  const formula = at(payload.formulas, row, col);
  if (formula === null) return value;
  return translateFormula(formula, payload.origin, row, col, dest);
}

/**
 * Offsets a formula source for its new home (Requirement 4.12).
 *
 * A source that no longer parses — it can only get here through a hand-built payload
 * or an external app's JSON — is written verbatim, so the dispatcher's own
 * classification reports the parse error at the offending cell rather than the
 * clipboard silently dropping it.
 */
export function translateFormula(
  source: string,
  origin: Addr | undefined,
  row: number,
  col: number,
  dest: Addr,
): string {
  if (origin === undefined) return source;
  const dRow = dest.row - (origin.row + row);
  const dCol = dest.col - (origin.col + col);
  if (dRow === 0 && dCol === 0) return source;
  const parsed = parseFormula(source);
  if (!parsed.ok) return source;
  return printFormula(translate(parsed.ast, dRow, dCol));
}

/** True when `range` lies inside the sheet limits. */
export function fitsSheet(range: Range): boolean {
  const r = normalizeRange(range);
  return r.r0 >= 0 && r.c0 >= 0 && r.r1 < MAX_ROWS && r.c1 < MAX_COLS;
}

/**
 * The payload an external `text/plain` paste produces (Requirement 12.8).
 *
 * No formulas and no styles: text from another application carries values only, and
 * each field goes through the dispatcher's `classifyInput` exactly as if it had been
 * typed, so a pasted `3/5/2024` becomes the same date a typed one does.
 */
export function payloadFromTable(table: readonly (readonly string[])[]): ClipPayload {
  const rows = table.length;
  const cols = table.reduce((max, row) => Math.max(max, row.length), 0);
  const inputs = table.map((row) =>
    row.length === cols ? [...row] : [...row, ...new Array<string>(cols - row.length).fill('')],
  );
  return { rows, cols, inputs };
}
